import api from '../../../utils/Api'
import { ID } from '../../../../_metronic/helpers'

// Account interfaces based on the actual API response structure
export interface Account {
  id: number
  name: string
  code: string
  mainAccount: string
  secondaryAccount: string
  minorAccount: string
  incomeType: string | null
  description: string | null
  whTax: number | null
  createdAt: string
  updatedAt: string
}

export interface AccountQueryResponse {
  data: Account[]
  meta?: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    first_page: number
    first_page_url: string
    last_page_url: string
    next_page_url: string | null
    previous_page_url: string | null
  }
}

export interface AccountQueryParams {
  page?: number
  per_page?: number
  search?: string
  mainAccount?: string
  secondaryAccount?: string
  minorAccount?: string
}

export interface CreateAccountRequest {
  name: string
  code: string
  mainAccount: string
  secondaryAccount: string
  minorAccount: string
  incomeType?: string
  description?: string
  whTax?: number
}

export interface UpdateAccountRequest {
  name?: string
  code?: string
  mainAccount?: string
  secondaryAccount?: string
  minorAccount?: string
  incomeType?: string
  description?: string
  whTax?: number
}

// Chart of Accounts structure for hierarchical display
export interface ChartOfAccountsResponse {
  data: ChartOfAccountCategory[]
}

export interface ChartOfAccountCategory {
  name: string
  code: string
  expanded?: boolean
  primaryItems: PrimaryAccountItem[]
}

export interface PrimaryAccountItem {
  id: string
  name: string
  code: string
  expanded?: boolean
  secondaryItems: SecondaryAccountItem[]
}

export interface SecondaryAccountItem {
  code: string
  name: string
  expanded?: boolean
  accounts: Account[]
}

// API Functions using api.request pattern
export const getAccounts = (params?: AccountQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-code', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, { s: params })
  })
}

export const getAccountById = (id: ID): Promise<Account | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-code/${id}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

export const createAccount = (data: CreateAccountRequest): Promise<Account | undefined> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-code', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

export const updateAccount = (id: ID, data: UpdateAccountRequest): Promise<Account | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-code/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'PATCH')
  })
}

export const deleteAccount = (id: ID): Promise<void> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-code/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve()
      } else {
        reject(response)
      }
    }, {}, 'DELETE')
  })
}

export const updateAccountName = (id: ID, name: string): Promise<Account | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-code/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form({ name }), 'PATCH')
  })
}

// Get chart of accounts in hierarchical structure
export const getChartOfAccounts = (): Promise<ChartOfAccountCategory[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      // เริ่มต้นด้วย static structure
      const staticStructure = getStaticChartOfAccounts()

      // ดึง dynamic accounts จาก API
      api.request('account/account-code', (data: any, success: boolean) => {
        if (success) {
          const accounts = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
          // Map dynamic accounts เข้ากับ static structure
          const result = mapDynamicAccountsToStaticStructure(staticStructure, accounts)
          resolve(result)
        } else {
          // ถ้า API ล้มเหลว ให้ใช้ static structure อย่างเดียว
          console.warn('API failed, using static structure only:', data)
          resolve(staticStructure)
        }
      })
    } catch (error) {
      // ถ้าเกิดข้อผิดพลาด ให้ใช้ static structure อย่างเดียว
      console.error('Error in getChartOfAccounts:', error)
      resolve(getStaticChartOfAccounts())
    }
  })
}

// Search accounts by term
export const searchAccounts = (searchTerm: string): Promise<Account[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-code/search', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, { search: searchTerm })
  })
}

// Get accounts by category
export const getAccountsByCategory = (mainAccount: string, secondaryAccount?: string, minorAccount?: string): Promise<Account[]> => {
  return new Promise((resolve, reject) => {
    const params: any = { mainAccount: mainAccount }
    if (secondaryAccount) params.secondaryAccount = secondaryAccount
    if (minorAccount) params.minorAccount = minorAccount

    api.request('account/account-code', (data: any, success: boolean) => {
      if (success) {
        resolve(data.data || data)
      } else {
        reject(data)
      }
    }, params)
  })
}

// Helper function to transform flat account data into hierarchical structure
export const transformAccountsToHierarchy = (accounts: Account[]): ChartOfAccountCategory[] => {
  const categories: { [key: string]: ChartOfAccountCategory } = {}

  accounts.forEach(account => {
    const mainKey = account.mainAccount
    const primaryKey = `${account.secondaryAccount}`
    const secondaryKey = `${account.minorAccount}`

    // Create main category if not exists
    if (!categories[mainKey]) {
      categories[mainKey] = {
        name: getMainAccountName(parseInt(account.mainAccount)),
        code: account.mainAccount,
        expanded: false,
        primaryItems: []
      }
    }

    // Find or create primary item
    let primaryItem = categories[mainKey].primaryItems.find(p => p.id === primaryKey)
    if (!primaryItem) {
      primaryItem = {
        id: primaryKey,
        name: getPrimaryAccountName(parseInt(account.mainAccount), parseInt(account.secondaryAccount)),
        code: `${account.mainAccount}${account.secondaryAccount}`,
        expanded: false,
        secondaryItems: []
      }
      categories[mainKey].primaryItems.push(primaryItem)
    }

    // Find or create secondary item
    let secondaryItem = primaryItem.secondaryItems.find(s => s.code === secondaryKey)
    if (!secondaryItem) {
      secondaryItem = {
        code: secondaryKey,
        name: getSecondaryAccountName(parseInt(account.mainAccount), parseInt(account.secondaryAccount), parseInt(account.minorAccount)),
        expanded: false,
        accounts: []
      }
      primaryItem.secondaryItems.push(secondaryItem)
    }

    // Add account to secondary item
    secondaryItem.accounts.push(account)
  })

  return Object.values(categories)
}

// Helper functions to get account names (you can customize these based on your business logic)
const getMainAccountName = (mainAccount: number): string => {
  const names: { [key: number]: string } = {
    1: 'สินทรัพย์',
    2: 'หนี้สิน',
    3: 'ส่วนของเจ้าของ',
    4: 'รายได้',
    5: 'ค่าใช้จ่าย',
    7: 'ค่าใช้จ่าย-ความงาม',
    8: 'ค่าใช้จ่าย Amazon',
    9: 'ค่าใช้จ่าย-Hostel'
  }
  return names[mainAccount] || `หมวดหมู่ ${mainAccount}`
}

const getPrimaryAccountName = (mainAccount: number, secondaryAccount: number): string => {
  // You can implement more specific naming logic here
  return `หมวดย่อย ${mainAccount}${secondaryAccount}`
}

const getSecondaryAccountName = (mainAccount: number, secondaryAccount: number, minorAccount: number): string => {
  // You can implement more specific naming logic here
  return `หมวดย่อยที่ ${mainAccount}${secondaryAccount}${minorAccount}`
}

// Static Chart of Accounts Data ตามโครงสร้างที่กำหนด
export const getStaticChartOfAccounts = (): ChartOfAccountCategory[] => {
  return [
    {
      name: 'สินทรัพย์',
      code: '1',
      expanded: false,
      primaryItems: [
        {
          id: '10',
          name: 'สินทรัพย์หมุนเวียน',
          code: '10',
          expanded: false,
          secondaryItems: [
            {
              code: '1011',
              name: 'เงินสด',
              expanded: false,
              accounts: []
            },
            {
              code: '1012',
              name: 'เงินฝากธนาคาร-ออมทรัพย์',
              expanded: false,
              accounts: []
            },
            {
              code: '1013',
              name: 'เงินฝากธนาคาร-กระแสรายวัน',
              expanded: false,
              accounts: []
            },
            {
              code: '1014',
              name: 'เงินลงทุนระยะสั้น',
              expanded: false,
              accounts: []
            },
            {
              code: '1021',
              name: 'ลูกหนี้การค้า',
              expanded: false,
              accounts: []
            },
            {
              code: '1022',
              name: 'ลูกหนี้พนักงาน',
              expanded: false,
              accounts: []
            },
            {
              code: '1023',
              name: 'ค่าใช้จ่ายล่วงหน้า',
              expanded: false,
              accounts: []
            },
            {
              code: '1024',
              name: 'ลูกหนี้อื่นๆ',
              expanded: false,
              accounts: []
            },
            {
              code: '1031',
              name: 'สินค้าคงเหลือ',
              expanded: false,
              accounts: []
            },
            {
              code: '1081',
              name: 'สินทรัพย์หมุนเวียนอื่นๆ',
              expanded: false,
              accounts: []
            },
            {
              code: '1091',
              name: 'ถึงกำหนดภายใน 1 ปี',
              expanded: false,
              accounts: []
            }
          ]
        },
        {
          id: '11',
          name: 'สินทรัพย์ไม่หมุนเวียน',
          code: '11',
          expanded: false,
          secondaryItems: [
            {
              code: '1111',
              name: 'เงินฝากธนาคารที่มีภาระผูกพัน',
              expanded: false,
              accounts: []
            },
            {
              code: '1112',
              name: 'เงินประกันและเงินมัดจำ',
              expanded: false,
              accounts: []
            },
            {
              code: '1113',
              name: 'ที่ดิน อาคาร และ อุปกรณ์(สุทธิ)',
              expanded: false,
              accounts: []
            },
            {
              code: '1115',
              name: 'สินทรัพย์ไม่มีตัวตน',
              expanded: false,
              accounts: []
            },
            {
              code: '1114',
              name: 'สินทรัพย์ไม่หมุนเวียนอื่น',
              expanded: false,
              accounts: []
            },
            {
              code: '1119',
              name: 'ค่าเสื่อมราคาสะสม',
              expanded: false,
              accounts: []
            },
            {
              code: '1120',
              name: 'สินทรัพย์ (ทรัพย์บนที่เช่า)',
              expanded: false,
              accounts: []
            },
            {
              code: '1121',
              name: 'หลักทรัพย์เพื่อการค้า',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'หนี้สิน',
      code: '2',
      expanded: false,
      primaryItems: [
        {
          id: '20',
          name: 'หนี้สินหมุนเวียน',
          code: '20',
          expanded: false,
          secondaryItems: [
            {
              code: '2011',
              name: 'เจ้าหนี้การค้าและตั๋วเงินจ่าย',
              expanded: false,
              accounts: []
            },
            {
              code: '2012',
              name: 'เจ้าหนี้อื่นๆ',
              expanded: false,
              accounts: []
            },
            {
              code: '2013',
              name: 'ค่าใช้จ่ายค้างจ่าย',
              expanded: false,
              accounts: []
            }
          ]
        },
        {
          id: '21',
          name: 'หนี้สินไม่หมุนเวียน',
          code: '21',
          expanded: false,
          secondaryItems: [
            {
              code: '2014',
              name: 'เงินกู้ยืมระยะยาว',
              expanded: false,
              accounts: []
            },
            {
              code: '2015',
              name: 'เจ้าหนี้เช่าซื้อ',
              expanded: false,
              accounts: []
            },
            {
              code: '2016',
              name: 'หนี้สิ้นไม่หมุนเวียนอื่น',
              expanded: false,
              accounts: []
            },
            {
              code: '2017',
              name: 'หนี้สิน (ทรัพย์บนที่เช่า)',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'ส่วนของเจ้าของ',
      code: '3',
      expanded: false,
      primaryItems: [
        {
          id: '30',
          name: 'ทุนเรือนหุ้น',
          code: '30',
          expanded: false,
          secondaryItems: [
            {
              code: '3011',
              name: 'ส่วนของผู้ถือหุ้น',
              expanded: false,
              accounts: []
            }
          ]
        },
        {
          id: '31',
          name: 'กำไร(ขาดทุนสะสม)',
          code: '31',
          expanded: false,
          secondaryItems: [
            {
              code: '3012',
              name: 'กำไร(ขาดทุน)สะสม',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'รายได้',
      code: '4',
      expanded: false,
      primaryItems: [
        {
          id: '40',
          name: 'รายได้จากการขาย/ให้บริการ',
          code: '40',
          expanded: false,
          secondaryItems: [
            {
              code: '4011',
              name: 'รายได้จากการขาย/ให้บริการ',
              expanded: false,
              accounts: []
            }
          ]
        },
        {
          id: '41',
          name: 'รายได้อื่นๆ',
          code: '41',
          expanded: false,
          secondaryItems: [
            {
              code: '4012',
              name: 'รายได้อื่นๆ',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'ค่าใช้จ่าย',
      code: '5',
      expanded: false,
      primaryItems: [
        {
          id: '50',
          name: 'ค่าใช้จ่ายทั่วไป',
          code: '50',
          expanded: false,
          secondaryItems: [
            {
              code: '5011',
              name: 'ต้นทุนสินค้าเพื่อการรักษา',
              expanded: false,
              accounts: []
            },
            {
              code: '5012',
              name: 'ต้นทุนบริการ',
              expanded: false,
              accounts: []
            },
            {
              code: '5013',
              name: 'ค่าใช้จ่ายในการขายและโฆษณา',
              expanded: false,
              accounts: []
            },
            {
              code: '5014',
              name: 'ค่าใช้จ่ายพนักงาน',
              expanded: false,
              accounts: []
            },
            {
              code: '5015',
              name: 'ค่าสาธารณูปโภค-สำนักงาน',
              expanded: false,
              accounts: []
            },
            {
              code: '5016',
              name: 'ค่าซ่อมแซมและบำรุงรักษา',
              expanded: false,
              accounts: []
            },
            {
              code: '5017',
              name: 'ค่าเสื่อมราคา',
              expanded: false,
              accounts: []
            },
            {
              code: '5018',
              name: 'ค่าเดินทาง',
              expanded: false,
              accounts: []
            },
            {
              code: '5019',
              name: 'ค่าใช้จ่ายงบการเงินและตลาดหลักทรัพย์',
              expanded: false,
              accounts: []
            },
            {
              code: '5020',
              name: 'ค่าประกันภัย',
              expanded: false,
              accounts: []
            },
            {
              code: '5021',
              name: 'ค่าใช้จ่ายเบ็ดเตล็ด-สำนักงาน',
              expanded: false,
              accounts: []
            },
            {
              code: '5022',
              name: 'ค่าใช้จ่ายอื่นๆ',
              expanded: false,
              accounts: []
            },
            {
              code: '5023',
              name: 'ค่าธรรมเนียมธนาคาร',
              expanded: false,
              accounts: []
            },
            {
              code: '5024',
              name: 'ดอกเบี้ยจ่าย',
              expanded: false,
              accounts: []
            },
            {
              code: '5025',
              name: 'ภาษี',
              expanded: false,
              accounts: []
            },
            {
              code: '5026',
              name: 'ค่าใช้จ่าย-ทรัพย์บนที่เช่า',
              expanded: false,
              accounts: []
            },
            {
              code: '5027',
              name: 'ค่าใช้จ่ายอื่น',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'ค่าใช้จ่าย-ความงาม',
      code: '7',
      expanded: false,
      primaryItems: [
        {
          id: '70',
          name: 'ค่าใช้จ่าย-ความงาม',
          code: '70',
          expanded: false,
          secondaryItems: [
            {
              code: '7011',
              name: 'ต้นทุนสินค้าเพื่อการรักษา-ความงาม',
              expanded: false,
              accounts: []
            },
            {
              code: '7012',
              name: 'ต้นทุนบริการ-ความงาม',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'ค่าใช้จ่าย Amazon',
      code: '8',
      expanded: false,
      primaryItems: [
        {
          id: '80',
          name: 'ค่าใช้จ่าย Amazon',
          code: '80',
          expanded: false,
          secondaryItems: [
            {
              code: '8011',
              name: 'ต้นทุนวัตถุดิบ-Amazon',
              expanded: false,
              accounts: []
            },
            {
              code: '8012',
              name: 'ต้นทุนสินค้า-Amazon',
              expanded: false,
              accounts: []
            },
            {
              code: '8013',
              name: 'ค่าใช้จ่ายในการขาย-Amazon',
              expanded: false,
              accounts: []
            },
            {
              code: '8014',
              name: 'ค่าใช้จ่ายบริหาร-Amazon',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    },
    {
      name: 'ค่าใช้จ่าย-Hostel',
      code: '9',
      expanded: false,
      primaryItems: [
        {
          id: '90',
          name: 'ค่าใช้จ่าย-Hostel',
          code: '90',
          expanded: false,
          secondaryItems: [
            {
              code: '9011',
              name: 'ต้นทุนบริการ-Hostel',
              expanded: false,
              accounts: []
            },
            {
              code: '9012',
              name: 'ค่าใช้จ่ายในการขาย-Hostel',
              expanded: false,
              accounts: []
            },
            {
              code: '9013',
              name: 'ค่าใช้จ่ายบริหาร-Hostel',
              expanded: false,
              accounts: []
            }
          ]
        }
      ]
    }
  ]
}

// ฟังก์ชันสำหรับ map ข้อมูล dynamic accounts เข้ากับ static structure
export const mapDynamicAccountsToStaticStructure = (staticData: ChartOfAccountCategory[], dynamicAccounts: Account[]): ChartOfAccountCategory[] => {
  const result = JSON.parse(JSON.stringify(staticData)) // Deep clone

  dynamicAccounts.forEach(account => {
    // ตรวจสอบว่าข้อมูลที่จำเป็นมีครบถ้วน
    if (!account || account.mainAccount == null || account.secondaryAccount == null || account.minorAccount == null) {
      console.warn('Account data is incomplete, skipping:', account)
      return
    }

    const mainAccountId = account.mainAccount
    const mainCategory = result.find((cat: ChartOfAccountCategory) => cat.code === mainAccountId)

    if (mainCategory) {
      // หา primary item ที่ตรงกับ secondaryAccount
      const primaryId = account.secondaryAccount
      let primaryItem = mainCategory.primaryItems.find((item: PrimaryAccountItem) => item.code === primaryId)

      if (!primaryItem) {
        // สร้าง primary item ใหม่ถ้าไม่มี
        primaryItem = {
          id: primaryId,
          name: `หมวดย่อย ${account.secondaryAccount}`,
          code: account.secondaryAccount,
          expanded: false,
          secondaryItems: []
        }
        mainCategory.primaryItems.push(primaryItem)
      }

      // หา secondary item ที่ตรงกับ minorAccount
      const minorAccountStr = account.minorAccount
      let secondaryItem = primaryItem.secondaryItems.find((item: SecondaryAccountItem) =>
        item.code === minorAccountStr
      )

      if (secondaryItem) {
        // เพิ่ม account เข้าไปใน secondary item ที่เจอ
        secondaryItem.accounts.push(account)
      } else {
        // หาชื่อจาก static data ก่อน ถ้าไม่มีใช้ชื่อเริ่มต้น
        const staticSecondaryItem = primaryItem.secondaryItems.find((item: SecondaryAccountItem) =>
          item.code === minorAccountStr
        )

        const secondaryItemName = staticSecondaryItem?.name || `หมวดย่อย ${account.minorAccount}`

        // สร้าง secondary item ใหม่ถ้าไม่มี
        const newSecondaryItem = {
          code: minorAccountStr,
          name: secondaryItemName,
          expanded: false,
          accounts: [account]
        }
        primaryItem.secondaryItems.push(newSecondaryItem)
      }
    } else {
      console.warn(`Main category ${mainAccountId} not found for account:`, account)
    }
  })

  // เรียงลำดับ secondary items ตาม code
  result.forEach((mainCategory: ChartOfAccountCategory) => {
    mainCategory.primaryItems.forEach((primaryItem: PrimaryAccountItem) => {
      primaryItem.secondaryItems.sort((a: SecondaryAccountItem, b: SecondaryAccountItem) => a.code.localeCompare(b.code))
    })
  })

  return result
}

// ===== DAILY JOURNAL / ACCOUNT BOOK API =====

// AccountBook interfaces
export interface AccountBookItem {
  id?: number
  accountBookId?: number
  paymentGatewayId?: number | null
  accountCode: string
  accountCodeName?: string
  description?: string | null
  debit: number
  credit: number
  createdAt?: string
  updatedAt?: string
  branchId?: number | null
  departmentId?: number | null
  parentId?: number | null
  oriDebit?: number | null
  oriCredit?: number | null
  children?: AccountBookItem[]
  projectId?: number | null
  cheques?: any[] | null
}

export interface AccountBook {
  id?: number
  bookType: string
  date: string
  code?: string
  labCompanyId?: number | null
  projectId?: number | null
  sellerId?: number | null
  departmentId?: number | null
  branchId?: number | null
  taxExpenseId?: number | null
  accountDesc?: string | null
  attachment?: any | null
  refDoc?: string | null
  saleChannel?: string | null
  state: string
  totalDebit: number
  totalCredit: number
  createdAt?: string
  updatedAt?: string
  items?: AccountBookItem[],
  taxInvoiceReference?: string | null
  cheques?: any[] | null
}

export interface AccountBookQueryParams {
  startDate?: string
  endDate?: string
  bookType?: string
  state?: string
  page?: number
  per_page?: number
  search?: string
}

export interface DailyJournalReportParams {
  startDate: string
  endDate: string
  bookType?: string
  state?: string
  sortBy?: 'date' | 'document'
}

export interface CreateAccountBookRequest {
  bookType: string
  date: string
  labCompanyId?: number | null
  projectId?: number | null
  sellerId?: number | null
  departmentId?: number | null
  branchId?: number | null
  taxExpenseId?: number | null
  accountDesc?: string | null
  refDoc?: string | null
  taxInvoiceReference?: string | null
  saleChannel?: string | null
  state: string
  totalDebit: number
  totalCredit: number
  items: Omit<AccountBookItem, 'id' | 'accountBookId' | 'createdAt' | 'updatedAt'>[]
}

export interface UpdateAccountBookRequest {
  bookType?: string
  date?: string
  labCompanyId?: number | null
  projectId?: number | null
  sellerId?: number | null
  departmentId?: number | null
  branchId?: number | null
  taxExpenseId?: number | null
  accountDesc?: string | null
  refDoc?: string | null
  taxInvoiceReference?: string | null
  saleChannel?: string | null
  state?: string
  totalDebit?: number
  totalCredit?: number
  items?: Omit<AccountBookItem, 'accountBookId' | 'createdAt' | 'updatedAt'>[]
}

export interface BulkUpdateAccountBookRequest {
  data: (CreateAccountBookRequest | (UpdateAccountBookRequest & { id: number }))[]
}

// AccountBook API Functions
export const getAccountBooks = (params?: AccountBookQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

export const getDailyJournalReport = (params: DailyJournalReportParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/daily-journal-report', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

export const getAccountBookById = (id: ID): Promise<AccountBook | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-book/${id}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

export const createAccountBook = (data: CreateAccountBookRequest): Promise<AccountBook | undefined> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

export const updateAccountBook = (id: ID, data: UpdateAccountBookRequest): Promise<AccountBook | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-book/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'PATCH')
  })
}

export const deleteAccountBook = (id: ID): Promise<void> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-book/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve()
      } else {
        reject(response)
      }
    }, {}, 'DELETE')
  })
}

export const bulkUpdateAccountBooks = (data: BulkUpdateAccountBookRequest): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/bulk-update', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

// Trial Balance API Functions
export interface TrialBalanceQueryParams {
  fromDate?: string
  toDate?: string
  trialBalanceDisplayType?: 'before_closing' | 'after_closing' | 'monthly_movement' | 'monthly_summary'
  periodType?: 'daily' | 'monthly' | 'quarterly' | 'yearly'
  accountCodeFrom?: string
  accountCodeTo?: string
  scopeType?: 'company' | 'company_by_branch' | 'branch'
  branchId?: number | null
  departmentIds?: number[]
}

export const getTrialBalance = (params?: TrialBalanceQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/trial-balance', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

// Balance Sheet API Functions
export interface BalanceSheetQueryParams {
  dates?: string[] // Array of dates in YYYY-MM-DD format
  showSubAccount?: boolean
  branchId?: number | null
  departmentIds?: number[]
  sort?: 'code' | 'name'
}

export const getBalanceSheet = (params?: BalanceSheetQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/balance-sheet', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params, 'POST')
  })
}

// Cash Flow Statement API Functions
export interface CashFlowStatementQueryParams {
  dates?: string[] // Array of dates in YYYY-MM-DD format
  showSubAccount?: boolean
  branchId?: number | null
  departmentIds?: number[]
  sort?: 'code' | 'name'
}

export const getCashFlowStatement = (params?: CashFlowStatementQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/cash-flow-statement', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params, 'POST')
  })
}

// Income Statement API Functions
export interface IncomeStatementQueryParams {
  dates?: string[] // Array of dates in YYYY-MM-DD format
  showSubAccount?: boolean
  branchId?: number | null
  departmentIds?: number[]
  sort?: 'code' | 'name'
}

export const getIncomeStatement = (params?: IncomeStatementQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/income-statement', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params, 'POST')
  })
}

// Update AccountBook State API
export interface UpdateAccountBookStateRequest {
  state: string
}

export const updateAccountBookState = (id: ID, data: UpdateAccountBookStateRequest): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-book/update-state/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

// Helper functions for AccountBook
export const getAccountBookStatusVariant = (status: string): string => {
  switch (status) {
    case 'draft': return 'warning'
    case 'pending': return 'info'
    case 'pending_approval': return 'primary'
    case 'should_consider': return 'danger'
    case 'approved': return 'success'
    case 'trash': return 'secondary'
    default: return 'secondary'
  }
}

// Map backend enum to Thai display text
export const getStatusDisplayName = (status: string): string => {
  const statusNames: { [key: string]: string } = {
    'draft': 'ร่าง',
    'pending': 'รอตรวจสอบ',
    'pending_approval': 'รออนุมัติ',
    'should_consider': 'ควรพิจารณา',
    'approved': 'อนุมัติแล้ว',
    'trash': 'ถังขยะ'
  }
  return statusNames[status] || status
}

// Map Thai display text to backend enum
export const getStatusFromDisplayName = (displayName: string): string => {
  const displayNameToStatus: { [key: string]: string } = {
    'ร่าง': 'draft',
    'รอตรวจสอบ': 'pending',
    'รออนุมัติ': 'pending_approval',
    'ควรพิจารณา': 'should_consider',
    'อนุมัติแล้ว': 'approved',
    'ถังขยะ': 'trash'
  }
  return displayNameToStatus[displayName] || displayName
}

export const getBookTypeDisplayName = (bookType: string): string => {
  const bookTypeNames: { [key: string]: string } = {
    'GENERAL': 'รายวันทั่วไป',
    'PURCHASE': 'รายวันซื้อ',
    'SALE': 'รายวันขาย',
    'PAYMENT': 'รายวันจ่าย',
    'RECEIVED': 'รายวันรับ',
    'EXPENSE': 'รายวันค่าใช้จ่าย',
    'ONLINE_PAYMENT': 'รายวันจ่าย-ขายสินค้าออนไลน์',
    'CHECK_PAYMENT': 'รายวันจ่ายเช็ค',
    'RETURN_RECEIVED': 'รายวันรับคืนสินค้า',
    'RETURN_SENT': 'รายวันส่งคืนสินค้า',
    'PETTY_CASH_PAYMENT': 'รายวันจ่าย-เงินสดย่อย',
    // Support lowercase as well
    'general': 'รายวันทั่วไป',
    'purchase': 'รายวันซื้อ',
    'sale': 'รายวันขาย',
    'payment': 'รายวันจ่าย',
    'received': 'รายวันรับ',
    'expense': 'รายวันค่าใช้จ่าย',
    'online_payment': 'รายวันจ่าย-ขายสินค้าออนไลน์',
    'check_payment': 'รายวันจ่ายเช็ค',
    'return_received': 'รายวันรับคืนสินค้า',
    'return_sent': 'รายวันส่งคืนสินค้า',
    'petty_cash_payment': 'รายวันจ่าย-เงินสดย่อย'
  }
  return bookTypeNames[bookType] || bookType
}

export const getBookTypeFromDisplayName = (displayName: string): string => {
  const displayNameToType: { [key: string]: string } = {
    'รายวันทั่วไป': 'general',
    'รายวันซื้อ': 'purchase',
    'รายวันขาย': 'sale',
    'รายวันจ่าย': 'payment',
    'รายวันรับ': 'received',
    'รายวันค่าใช้จ่าย': 'expense',
    'รายวันจ่าย-ขายสินค้าออนไลน์': 'online_payment',
    'รายวันจ่ายเช็ค': 'check_payment',
    'รายวันรับคืนสินค้า': 'return_received',
    'รายวันส่งคืนสินค้า': 'return_sent',
    'รายวันจ่าย-เงินสดย่อย': 'petty_cash_payment'
  }
  return displayNameToType[displayName] || displayName
}

// ===== LAB COMPANY API =====

// LabCompany interface
export interface LabCompany {
  id: number
  name: string
  code?: string
  address?: string
  phone?: string
  email?: string
  createdAt?: string
  updatedAt?: string
}

// LabCompany API Functions
export const getLabCompanyById = (id: ID): Promise<LabCompany | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`lab-company/${id}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

export interface GeneralLedgerQueryParams {
  startDate?: string
  endDate?: string
  branchId?: number | null
  departmentId?: number | null
  departmentIds?: number[]
}

export const getAllAccountBookItems = (params?: GeneralLedgerQueryParams): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/account-book-items', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

// ===== GENERAL LEDGER API =====

// General Ledger interfaces
export interface GeneralLedgerAccountItem {
  id?: number
  accountBookId: number
  accountCode: string
  description?: string | null
  debit: number
  credit: number
  createdAt: string
  bookDate?: string
  updatedAt?: string
  bookCode?: string
  branchId?: number | null
  departmentId?: number | null
  branchName?: string | null
  departmentName?: string | null
  allocationName?: string | null
}

export interface GeneralLedgerAccount {
  accountCode: string
  accountName: string
  items: GeneralLedgerAccountItem[]
}

// Get AccountBook code by ID
export const getAccountBookCode = (accountBookId: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-book/${accountBookId}`, (data: any, success: boolean) => {
      if (success && data) {
        // Return the book code, fallback to generated code if not available
        const bookCode = data.code || `AB-${accountBookId}`
        resolve(bookCode)
      } else {
        // Fallback to generated code if API fails
        resolve(`AB-${accountBookId}`)
      }
    })
  })
}

export interface AccountBookItemsRemain {
  accountCode: string,
  accountName: string,
  remain: number,
  change: number,
  description?: string
}

export const getAccountBookItemsRemain = (): Promise<AccountBookItemsRemain[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/account-book/account-book-items/remain', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    })
  })
}
